import { Component, EventEmitter, Input, Output } from "@angular/core";
import { extractErrorMessage } from "@bbwt/modules/data-service";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { getTitle } from "@main/users";
import { FormButtonsConfig } from "@mipro/components";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { Message } from "primeng/api";
import { map } from "rxjs";
import { IncompleteDataService } from "./incomplete-data.service";
import { DialogModule } from "primeng/dialog";
import { CommonModule } from "@angular/common";
import { MessagesModule } from "primeng/messages";
import { MiproSharedModule } from "@mipro/mipro-shared.module";
import { CheckboxModule } from "primeng/checkbox";
import { FormsModule } from "@angular/forms";

@Component({
    selector: "request-access-dialog",
    standalone: true,
    imports: [DialogModule, CommonModule, MessagesModule, MiproSharedModule, CheckboxModule, FormsModule],
    providers: [IncompleteDataService],
    template: `<p-dialog
        [(visible)]="visible"
        [style]="{ 'max-width': '700px', width: '100%' }"
        [modal]="true"
        appendTo="body"
        header="Request Access">
        <div class="pt-2" *miproLoading="loadingPatientInfo">
            <p-messages *ngIf="errors?.length" [value]="errors" [enableService]="false" [closable]="false"></p-messages>

            <ng-container *ngIf="patientInfo">
                <p>
                    Please confirm that you consent to request <b>{{ patientInfo }}</b> to give you and your {{ orgLabel$ | async }} access
                    to view all ORB Clinical/Medical Data held in the system?
                </p>
                <p>
                    <p-checkbox
                        [binary]="true"
                        label="Request Access"
                        [(ngModel)]="requestConsent"
                        [ngModelOptions]="{ standalone: true }"></p-checkbox>
                </p>

                <form-buttons [config]="buttonsConfig" [disabled]="!requestConsent" [saving]="grantingAccess"> </form-buttons>
            </ng-container>
        </div>
    </p-dialog> `
})
export class RequestAccessDialogComponent {
    private _visible = false;

    requestConsent = false;
    errors: Message[] = null;
    loadingPatientInfo = false;
    patientInfo: string = null;
    buttonsConfig = <FormButtonsConfig>{
        cancelFunc: () => (this.visible = false),
        saveLabel: "Request Access",
        saveFunc: () => this.requestAccess()
    };
    grantingAccess = false;
    orgLabel$ = this.namingConvention.namingConvention$.pipe(map(nc => nc.singularOrganisationLabel));

    @Input() set visible(value: boolean) {
        this._visible = value;
        if (this._visible) {
            this.initRequestAccess();
        }
        this.visibleChange.next(value);
    }
    get visible() {
        return this._visible;
    }

    @Output() readonly visibleChange = new EventEmitter<boolean>();
    @Output() readonly onProceed = new EventEmitter<void>();

    constructor(
        private patientService: PatientService,
        private incompleteDataService: IncompleteDataService,
        private namingConvention: NamingConventionService
    ) {}

    private async initRequestAccess() {
        this.requestConsent = false;
        this.errors = null;
        this.patientInfo = null;

        try {
            this.loadingPatientInfo = true;
            const info = await this.incompleteDataService.getPatientInfo(this.patientService.currentPatientId);
            this.patientInfo = getTitle(info.title) + " " + info.firstName + " " + info.lastName;
        } catch (err: unknown) {
            const detail = extractErrorMessage(err, "An unknown error has occurred while loading PATIENT information");
            this.errors = [{ severity: "error", detail }];
        } finally {
            this.loadingPatientInfo = false;
        }
    }

    private async requestAccess() {
        this.grantingAccess = true;

        try {
            await this.incompleteDataService.requestManualAccess(this.patientService.currentPatientId);

            this.visible = false;
            this.onProceed.next();
        } finally {
            this.grantingAccess = false;
        }
    }
}
